import { useEffect, useMemo } from 'react';

import { CreditBookmark } from 'types/credit';
import { isBookmarkValid } from 'utils/credit-bookmark';
import { usePersistentStore } from 'store/persistent';
import useReferenceData from 'hooks/use-reference-data';

export default function useCreditBookmarks(): {
  creditBookmarks: Map<string, CreditBookmark>;
  addCreditBookmark: (creditBookmark: CreditBookmark) => void;
  removeCreditBookmark: (creditBookmarkID: string) => void;
  setCreditBookmarks: (creditBookmarks: CreditBookmark[]) => void;
  // eslint-disable-next-line indent
} {
  const referenceData = useReferenceData();

  const creditBookmarks = usePersistentStore((state) => state.creditBookmarks);
  const addCreditBookmark = usePersistentStore((state) => state.addCreditBookmark);
  const removeCreditBookmark = usePersistentStore((state) => state.removeCreditBookmark);
  const setCreditBookmarks = usePersistentStore((state) => state.setCreditBookmarks);

  const creditBookmarksMap = useMemo<Map<string, CreditBookmark>>(
    () => new Map(creditBookmarks.map((bookmark) => [bookmark.creditItemID, bookmark])),
    [creditBookmarks],
  );

  // Clear out any invalid bookmarks due to stale reference data
  useEffect(() => {
    if (!referenceData) {
      return;
    }
    const validBookmarks = creditBookmarks.filter((bookmark) => isBookmarkValid(bookmark, referenceData), []);
    if (validBookmarks.length !== creditBookmarks.length) {
      setCreditBookmarks(validBookmarks);
    }
  }, [creditBookmarks, referenceData, setCreditBookmarks]);

  return {
    creditBookmarks: creditBookmarksMap,
    addCreditBookmark,
    removeCreditBookmark,
    setCreditBookmarks,
  };
}
